import { useEffect, useRef, useState } from 'react';
import { apiGet, operationsClient } from '@/lib/api/client';
import { settingsEndpoints } from '@/lib/api/endpoints';

export interface EntityPreviewItem {
  id?: string;
  qbId?: string;
  name: string;
  detail: string;
  /** Entity's natural date (TxnDate or QB CreateTime). ISO 8601 or null. */
  sortDate?: string | null;
}

export interface EntityPreviewPage {
  items: EntityPreviewItem[];
  total: number;
  nextCursor: string | null;
}

/**
 * Paginated sync preview for a single entity in either direction. Drives
 * the slider dialog so it can scroll through 10k+ records without the
 * API returning the whole set at once.
 *
 * Push direction queries the local Postgres (cheap, cursor-stable).
 * Pull direction queries QB via the SQL Query API with
 * STARTPOSITION/MAXRESULTS paging — same response shape so the dialog
 * component is shared.
 *
 * Items / total / nextCursor stay in place during refetch (only
 * `isFetching` flips) so the dialog body doesn't reflow when the slider
 * moves. Stale responses are dropped via a request-id ref.
 */
export function useEntitySyncPreview(
  entity: string | null,
  direction: 'push' | 'pull',
  sinceIso: string | null,
  enabled: boolean,
) {
  const [debouncedSince, setDebouncedSince] = useState(sinceIso);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSince(sinceIso), 400);
    return () => clearTimeout(debounceRef.current);
  }, [sinceIso]);

  const [items, setItems] = useState<EntityPreviewItem[]>([]);
  const [total, setTotal] = useState(0);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const requestIdRef = useRef(0);
  // Cursor stack for paged "Previous / Next" navigation. Each entry is
  // the cursor that was used to fetch a given page (null = page 1). Index
  // [0] = page 1, [1] = page 2, etc. Cleared whenever inputs reset so
  // pagination doesn't outlive the slider window.
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [pageIndex, setPageIndex] = useState(0);

  const endpointFor = (e: string) =>
    direction === 'push'
      ? settingsEndpoints.qbEntityPushPreview(e)
      : settingsEndpoints.qbEntityPullPreview(e);

  // Reset + load page 1 whenever the inputs change. Items stay in place
  // until new data arrives so the dialog stays at a stable height.
  useEffect(() => {
    if (!enabled || !entity || !debouncedSince) {
      setItems([]);
      setTotal(0);
      setNextCursor(null);
      setIsFetching(false);
      setError(null);
      setCursorStack([null]);
      setPageIndex(0);
      return;
    }
    const myRequestId = ++requestIdRef.current;
    setError(null);
    setIsFetching(true);
    setCursorStack([null]);
    setPageIndex(0);
    apiGet<EntityPreviewPage>(operationsClient, endpointFor(entity), {
      params: { since: debouncedSince, limit: '50' },
    })
      .then((page) => {
        if (requestIdRef.current !== myRequestId) return;
        setItems(page.items);
        setTotal(page.total);
        setNextCursor(page.nextCursor);
      })
      .catch((err) => {
        if (requestIdRef.current !== myRequestId) return;
        setError(err);
      })
      .finally(() => {
        if (requestIdRef.current !== myRequestId) return;
        setIsFetching(false);
      });
    // endpointFor closes over `direction`; depending on direction is
    // sufficient (and cleaner than memoizing the function).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, entity, direction, debouncedSince]);

  /**
   * "Load more" — append the next page to the current items. Used by the
   * Enable-sync dialog where the operator scrolls a single growing list.
   */
  const loadMore = () => {
    if (!entity || !debouncedSince || !nextCursor || isFetching) return;
    const myRequestId = requestIdRef.current;
    setIsFetching(true);
    apiGet<EntityPreviewPage>(operationsClient, endpointFor(entity), {
      params: {
        since: debouncedSince,
        cursor: nextCursor,
        limit: '50',
      },
    })
      .then((page) => {
        if (requestIdRef.current !== myRequestId) return;
        setItems((prev) => [...prev, ...page.items]);
        setTotal(page.total);
        setNextCursor(page.nextCursor);
      })
      .catch((err) => {
        if (requestIdRef.current !== myRequestId) return;
        setError(err);
      })
      .finally(() => {
        if (requestIdRef.current !== myRequestId) return;
        setIsFetching(false);
      });
  };

  /**
   * Page-based navigation — `nextPage` / `prevPage` REPLACE the items
   * with the next/previous page. Used by the Manual Sync table where
   * the operator wants paged Prev/Next nav, not an ever-growing list.
   *
   * Cursor stack records the cursor used for each page so `prevPage`
   * can re-fetch the previous page deterministically (the API is
   * forward-only — no "previous cursor" — so we replay from a known
   * earlier cursor).
   */
  const fetchPage = (cursor: string | null, newPageIndex: number) => {
    if (!entity || !debouncedSince) return;
    const myRequestId = ++requestIdRef.current;
    setIsFetching(true);
    setError(null);
    apiGet<EntityPreviewPage>(operationsClient, endpointFor(entity), {
      params: {
        since: debouncedSince,
        ...(cursor ? { cursor } : {}),
        limit: '50',
      },
    })
      .then((page) => {
        if (requestIdRef.current !== myRequestId) return;
        setItems(page.items);
        setTotal(page.total);
        setNextCursor(page.nextCursor);
        setPageIndex(newPageIndex);
      })
      .catch((err) => {
        if (requestIdRef.current !== myRequestId) return;
        setError(err);
      })
      .finally(() => {
        if (requestIdRef.current !== myRequestId) return;
        setIsFetching(false);
      });
  };

  const nextPage = () => {
    if (!nextCursor || isFetching) return;
    setCursorStack((prev) => {
      // Avoid pushing the same cursor twice if user clicks fast.
      if (prev[prev.length - 1] === nextCursor) return prev;
      return [...prev, nextCursor];
    });
    fetchPage(nextCursor, pageIndex + 1);
  };

  const prevPage = () => {
    if (pageIndex === 0 || isFetching) return;
    const newPageIndex = pageIndex - 1;
    const cursor = cursorStack[newPageIndex] ?? null;
    setCursorStack((prev) => prev.slice(0, newPageIndex + 1));
    fetchPage(cursor, newPageIndex);
  };

  return {
    items,
    total,
    nextCursor,
    pageIndex,
    hasPrev: pageIndex > 0,
    hasNext: Boolean(nextCursor),
    isFetching,
    isDebouncing: sinceIso !== debouncedSince,
    error,
    loadMore,
    nextPage,
    prevPage,
  };
}
